import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Download, Copy, Check, Waves, Code2, Film } from "lucide-react";
import { toast } from "sonner";
import { SOCIAL_ASSETS, REELS } from "@/backyardData";

export default function SocialPack() {
  const [copied, setCopied] = useState(null);
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const embedSnippet = `<iframe src="${origin}/embed" width="100%" height="620" style="border:0;border-radius:16px;overflow:hidden" title="Just Spas Virtual Showroom" allowfullscreen></iframe>`;

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(null), 1500);
    } catch (_) {
      toast.error("Copy failed — select and copy manually");
    }
  };

  return (
    <div className="min-h-screen bg-[#16171c] pb-20 text-white">
      <header className="sticky top-0 z-30 border-b border-white/10 bg-black/50 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
          <Link to="/" data-testid="social-back-link" className="flex items-center gap-2 text-sm text-slate-300 hover:text-white">
            <ArrowLeft size={16} /> <span className="hidden sm:inline">Showroom</span>
          </Link>
          <div className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-cyan-500/15 text-cyan-300"><Waves size={16} /></span>
            <span className="font-display text-sm font-semibold tracking-tight sm:text-base">Social Content Pack</span>
          </div>
          <span className="w-16" />
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <motion.h1 initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="font-display text-4xl font-bold tracking-tight sm:text-5xl">
          Social content pack
        </motion.h1>
        <p className="mt-2 max-w-2xl text-sm text-slate-400">
          Ready-to-post cinematic hero images for Facebook, Instagram &amp; TikTok, plus an embed snippet to drop the showroom portal onto your website. Each image links back to the virtual showroom.
        </p>

        {/* Embed snippet */}
        <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
          <div className="mb-2 flex items-center gap-2 text-xs uppercase tracking-widest text-cyan-300"><Code2 size={14} /> Homepage portal embed</div>
          <p className="mb-3 text-sm text-slate-400">Paste this into your website to add an "Enter the Virtual Showroom" portal hero (a live preview is at <Link to="/embed" className="text-cyan-300 underline">/embed</Link>).</p>
          <div className="flex flex-col gap-2 sm:flex-row">
            <code className="flex-1 overflow-x-auto rounded-lg border border-white/10 bg-black/40 p-3 text-xs text-slate-300 no-scrollbar">{embedSnippet}</code>
            <button onClick={() => copy(embedSnippet, "embed")} data-testid="copy-embed" className="inline-flex items-center justify-center gap-2 rounded-full bg-cyan-500 px-4 py-2 text-sm font-semibold text-black hover:bg-cyan-400">
              {copied === "embed" ? <Check size={16} /> : <Copy size={16} />} Copy
            </button>
          </div>
        </div>

        {/* Image gallery */}
        <h2 className="mt-10 font-display text-2xl font-semibold tracking-tight">Post-ready images</h2>
        <div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {SOCIAL_ASSETS.map((a) => (
            <div key={a.slug} className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03]" data-testid={`social-card-${a.slug}`}>
              <div className={`relative overflow-hidden bg-black ${a.ratio === "portrait" ? "aspect-[9/16]" : "aspect-square"}`}>
                <img src={a.url} alt={a.title} loading="lazy" className="h-full w-full object-cover" />
              </div>
              <div className="p-3">
                <div className="text-sm font-medium">{a.title}</div>
                <p className="mt-1 line-clamp-3 text-xs text-slate-400">{a.caption}</p>
                <div className="mt-3 flex gap-2">
                  <a href={a.url} target="_blank" rel="noreferrer" download className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3 py-2 text-xs hover:bg-white/10">
                    <Download size={13} /> Image
                  </a>
                  <button onClick={() => copy(a.caption, a.slug)} className="inline-flex items-center justify-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3 py-2 text-xs hover:bg-white/10">
                    {copied === a.slug ? <Check size={13} /> : <Copy size={13} />} Caption
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Video reels */}
        <h2 className="mt-10 flex items-center gap-2 font-display text-2xl font-semibold tracking-tight">
          <Film size={20} className="text-amber-300" /> Video reels
        </h2>
        <p className="mt-1 max-w-2xl text-sm text-slate-400">Short looping aerial clips — ready to post to Reels, TikTok &amp; Stories.</p>
        <div className="mt-4 grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4">
          {REELS.map((r) => (
            <div key={r.slug} className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03]" data-testid={`reel-card-${r.slug}`}>
              <div className="relative aspect-[9/16] bg-black">
                <video
                  src={r.video}
                  poster={r.poster}
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  className="h-full w-full object-cover"
                  data-testid={`reel-video-${r.slug}`}
                />
              </div>
              <div className="p-3">
                <div className="text-sm font-medium">{r.title}</div>
                <p className="mt-1 line-clamp-2 text-xs text-slate-400">{r.caption}</p>
                <div className="mt-3 flex gap-2">
                  <a href={r.video} target="_blank" rel="noreferrer" download className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3 py-2 text-xs hover:bg-white/10" data-testid={`reel-download-${r.slug}`}>
                    <Download size={13} /> Video
                  </a>
                  <button onClick={() => copy(r.caption, r.slug)} className="inline-flex items-center justify-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3 py-2 text-xs hover:bg-white/10">
                    {copied === r.slug ? <Check size={13} /> : <Copy size={13} />} Caption
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
